/**
 * PHYSICAL-RISK-POPULATION-CATALOG-MODAL
 * Show population templates from the DUERP catalogue.
 */
import CardButton from "@/components/card-button"
import CardContainer from "@/components/card-container"
import Card from "@/components/card"
import Checkbox from "@/components/checkbox"
import env from "@/env"
import Link from "@/components/link"
import ListItem from "@/components/list-item"
import Modal from "@/components/modal"
import ModalConfirm from "@/components/modal-confirm"
import Space from "@/components/space"
import LoadingModal from "@/modals/loading.modal"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { SocketCreate, SocketEmit } from "@/utils/socketio.utils"
import { ASSISTANT_JUDGMENT } from "@/utils/assistant.utils"
import {
  createEmptyPopulation,
  Population,
  PopulationTemplate,
  PopulationTemplateCategory,
  PopulationTemplateCatalog,
  PopulationTemplateRisk
} from "@/entities/population/model/population.types"
import populationTemplatesJson from "@/entities/population/model/population.template.json"
import { faCheck } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { groupBy, sortBy } from "lodash"
import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import { getRiskAttributeNames } from "@/redux/account.types"
import { matchKey } from "@/entities/establishment/model/duerp-mapping.utils"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import riskFamilyStore from "@/entities/risk-family/model/risk-family.store"
import { RISK_FAMILY_TEMPLATES } from "@/entities/risk-family/model/risk-family.utils"
import { createEmptyRiskFamily } from "@/entities/risk-family/model/risk-family.types"
import populationStore from "@/entities/population/model/population.store"
import physicalRiskStore from "../../model/physical-risk.store"
import { createEmptyPhysicalRisk, PhysicalRisk } from "../../model/physical-risk.types"
import { getLowestPreventionValue, mapNamedAttributesToIds } from "../../model/physical-risk.utils"

const CATALOG = populationTemplatesJson as PopulationTemplateCatalog
type Step = "CATEGORIES" | "RISKS"
type AssistantRisk = PhysicalRisk & {
  populationCode: string
  riskFamilyCode?: string
  riskFamilyName?: string
}
type AssistantImport = {
  populations: Population[]
  populationCodes: string[]
  risks: AssistantRisk[]
}

interface Props {
  siteId?: string | null
  siteName?: string | null
  populationId?: string | null
  populationName?: string | null
  familyId?: string | null
  familyName?: string | null
  industryCode?: string | null
  onClose: () => void
  onNext?: (risk: PhysicalRisk) => void
}

interface PopulationTemplateCategoryWithTemplates extends PopulationTemplateCategory {
  templates: PopulationTemplate[]
}

function normalizeNafCode(code?: string | null) {
  return (code ?? "").replace(/\s/g, "").replace(/\./g, "").toUpperCase()
}

function getRiskId(population: PopulationTemplate, riskIndex: number) {
  return `${population.code}-${riskIndex}`
}

function getPopulationImage(code: string) {
  return env.REACT_APP_URL_SPACE.concat("/PopulationTemplate/", code, ".png")
}

function getRiskFamilyImage(code: string) {
  return env.REACT_APP_URL_SPACE.concat("/RiskFamilies/", code, ".png")
}

function getInitialSelectedItems(industryCode?: string | null, existingPopulations: Population[] = []) {
  const nafCode = normalizeNafCode(industryCode)
  if (!nafCode) return []

  // Smart default: select NAF-compatible catalogue populations that do not exist yet.
  const existingPopulationNames = new Set(existingPopulations.map((population) => matchKey(population.name)))

  return CATALOG.populations
    .filter((population) => !existingPopulationNames.has(matchKey(population.name)))
    .filter((population) => (population.nafCodes ?? []).some((code) => normalizeNafCode(code) === nafCode))
    .map((population) => population.code)
}

function getCategoriesWithTemplates(): PopulationTemplateCategoryWithTemplates[] {
  const templatesByCategory = groupBy(CATALOG.populations, "category")

  return sortBy(
    CATALOG.categories
      .map((category) => ({
        ...category,
        templates: templatesByCategory[category.code] ?? []
      }))
      .filter((category) => category.templates.length > 0),
    "label"
  )
}

function extractJsonObject(response: string) {
  const sanitized = response
    .trim()
    .replace(/^```(?:json)?/i, "")
    .replace(/```$/i, "")
    .trim()
  const start = sanitized.indexOf("{")
  const end = sanitized.lastIndexOf("}")
  if (start === -1 || end === -1) throw new Error("No JSON object found")
  return JSON.parse(sanitized.slice(start, end + 1))
}

function truncatePromptText(text?: string, maxLength = 320) {
  if (!text) return undefined
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text
}

export default function PhysicalRiskPopulationCatalogModal(props: Props) {
  const dispatch = useAppDispatch()

  const accountRisksSettings = useAppSelector((state) => state._session.accountRisksSettings)
  const activeEstablishment = useAppSelector(establishmentStore.selectors.active)
  const riskFamilies = useAppSelector(riskFamilyStore.selectors.list)
  const existingPopulations = useAppSelector(populationStore.selectors.listForActiveEstablishment)
  const selectedPopulationIds = useAppSelector(populationStore.selectors.selected)
  const isSiteContext = !!props.siteId
  const isPopulationContext = !!props.populationId
  const isFamilyContext = !!props.familyId
  const shouldCreatePopulations = !isSiteContext && !isPopulationContext

  // Step 1: catalogue population selection.
  const [selectedItems, setSelectedItems] = useState<string[]>(() =>
    shouldCreatePopulations && !isFamilyContext ? getInitialSelectedItems(props.industryCode, existingPopulations) : []
  )

  // Step 2: detailed import selection. It is intentionally separate from step 1.
  const [selectedRiskPopulations, setSelectedRiskPopulations] = useState<string[]>([])
  const [selectedRisks, setSelectedRisks] = useState<string[]>([])
  const [step, setStep] = useState<Step>("CATEGORIES")
  const [populationCategoryModal, setPopulationCategoryModal] =
    useState<PopulationTemplateCategoryWithTemplates | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [generatedImport, setGeneratedImport] = useState<AssistantImport | null>(null)

  const categories = useMemo(() => getCategoriesWithTemplates(), [])
  const selectedTemplates = useMemo(
    () =>
      sortBy(
        CATALOG.populations.filter((population) => selectedItems.includes(population.code)),
        "name"
      ),
    [selectedItems]
  )
  const selectedCategories = useMemo(() => {
    const templatesByCategory = groupBy(selectedTemplates, "category")

    return categories
      .map((category) => ({
        ...category,
        templates: templatesByCategory[category.code] ?? []
      }))
      .filter((category) => category.templates.length > 0)
  }, [categories, selectedTemplates])
  const generatedNewPopulationCount = useMemo(() => {
    if (!generatedImport) return 0
    const existingPopulationNames = new Set(existingPopulations.map((population) => matchKey(population.name)))
    if (!shouldCreatePopulations) return 0
    return generatedImport.populations.filter(
      (population) => !existingPopulationNames.has(matchKey(population.name))
    ).length
  }, [generatedImport, existingPopulations, shouldCreatePopulations])

  // Population selection in the catalogue overview.
  function handleSelectPopulation(population: PopulationTemplate) {
    setSelectedItems((items) =>
      items.includes(population.code) ? items.filter((code) => code !== population.code) : items.concat(population.code)
    )
  }

  function handleSelectCategory(category: PopulationTemplateCategoryWithTemplates) {
    if (category.templates.length === 1) {
      handleSelectPopulation(category.templates[0])
      return
    }

    setPopulationCategoryModal(category)
  }

  // Population selection in the detailed risk step.
  function getSelectedCategoryTemplates(category: PopulationTemplateCategoryWithTemplates) {
    return sortBy(
      category.templates.filter((population) => selectedItems.includes(population.code)),
      "name"
    )
  }

  // Step 2 has its own population state: unchecking here only hides its risks.
  function togglePopulationAndRisks(population: PopulationTemplate) {
    const isSelected = selectedRiskPopulations.includes(population.code)
    const populationRiskIds = population.risks.map((_, index) => getRiskId(population, index))

    setSelectedRiskPopulations((items) =>
      isSelected ? items.filter((code) => code !== population.code) : items.concat(population.code)
    )
    setSelectedRisks((items) =>
      isSelected
        ? items.filter((riskId) => !populationRiskIds.includes(riskId))
        : Array.from(new Set(items.concat(populationRiskIds)))
    )
  }

  function toggleRisk(population: PopulationTemplate, riskIndex: number) {
    const riskId = getRiskId(population, riskIndex)
    setSelectedRisks((items) => (items.includes(riskId) ? items.filter((id) => id !== riskId) : items.concat(riskId)))
  }

  // Start the detailed risk selection with every selected population/risk checked.
  function startRiskSelection() {
    setSelectedRiskPopulations(selectedTemplates.map((population) => population.code))
    setSelectedRisks(
      selectedTemplates.flatMap((population) => population.risks.map((_, index) => getRiskId(population, index)))
    )
    setStep("RISKS")
  }

  function getSelectedRiskTemplates() {
    return selectedTemplates
      .map((population) => ({
        code: population.code,
        name: population.name,
        risks: population.risks
          .map((risk, riskIndex) => ({ risk, riskIndex }))
          .filter(({ riskIndex }) => selectedRisks.includes(getRiskId(population, riskIndex)))
          .map(({ risk }) => ({
            d: truncatePromptText(risk.description),
            details: truncatePromptText(risk.details),
            consequences: truncatePromptText(risk.conséquences),
            family: risk.riskFamily
          }))
      }))
      .filter((population) => population.risks.length > 0)
  }

  function getSelectedRiskFamilyFallbacks() {
    return selectedTemplates.flatMap((population) =>
      population.risks
        .map((risk, riskIndex) => ({ risk, riskIndex }))
        .filter(({ riskIndex }) => selectedRisks.includes(getRiskId(population, riskIndex)))
        .map(({ risk }) => risk.riskFamily)
    )
  }

  async function generateImport() {
    setIsGenerating(true)

    const promptContext = props.siteId
      ? { type: "site", id: props.siteId, name: props.siteName }
      : props.populationId
        ? { type: "population", id: props.populationId, name: props.populationName }
        : props.familyId
          ? { type: "family", id: props.familyId, name: props.familyName }
          : { type: "generic" }

    const prompt = `Import DUERP IDTree depuis sélection INRS.

JSON seul:
{ "populations": [{ "code": "", "name": "", "description": "", "size": 0, "attributes": [] }], "risks": [{ "populationCode": "", "attributes": {}, "severity": 1, "frequency": 1, "prevention": 1, "riskFamilyId": "" }] }

Règles: attributes=${JSON.stringify(getRiskAttributeNames(accountRisksSettings.riskAttributes))}. Notes dans les valeurs d'échelles. Brut=severity*frequency. riskFamilyId obligatoire.${props.familyId ? ` Utiliser exclusivement riskFamilyId="${props.familyId}".` : ""}

${JSON.stringify({
  context: promptContext,
  naf: isFamilyContext ? null : props.industryCode,
  scales: [
    accountRisksSettings.severityScale,
    accountRisksSettings.frequencyScale,
    accountRisksSettings.preventionScale,
    accountRisksSettings.residualRiskScale
  ],
  families: riskFamilies.map((family) => [family.id, family.code, family.name]),
  familyCatalog: RISK_FAMILY_TEMPLATES.map((family) => [family.code, family.name]),
  existing: existingPopulations.map((population) => population.name),
  selection: getSelectedRiskTemplates()
})}`

    try {
      const socket = SocketCreate()
      const response = await new Promise<string>((resolve, reject) => {
        let stream = ""
        SocketEmit(
          socket,
          "task-custom",
          {
            ...ASSISTANT_JUDGMENT,
            preprompt: `Retourner uniquement un JSON valide au format { "populations": [], "risks": [] }`,
            prompt,
            sendMode: "all"
          },
          "fr",
          2500,
          (res: string) => {
            stream = res
          },
          (err: unknown) => reject(err),
          () => resolve(stream)
        )
      })
      const parsed = extractJsonObject(response)
      const populations = Array.isArray(parsed.populations) ? parsed.populations : []
      const risks = Array.isArray(parsed.risks) ? parsed.risks : []
      if (!populations.length || !risks.length) {
        toast("L'assistant n'a pas généré de fonctions ou de risques.", { type: "error" })
        return
      }
      const riskFamilyFallbacks = getSelectedRiskFamilyFallbacks()
      setGeneratedImport({
        populations: populations.map((population) =>
          createEmptyPopulation({
            name: population.name ?? "",
            description: population.description ?? "",
            size: 0,
            attributes: population.attributes ?? [],
            EstablishmentId: activeEstablishment?.id ?? null
          })
        ),
        populationCodes: populations.map((population) => population.code ?? ""),
        risks: risks.map((risk, riskIndex) => {
          const familyKey =
            props.familyId ?? risk.riskFamilyId ?? risk.riskFamilyCode ?? riskFamilyFallbacks[riskIndex] ?? null
          return {
            ...createEmptyPhysicalRisk({
              attributes: mapNamedAttributesToIds(risk.attributes, accountRisksSettings.riskAttributes),
              severity: risk.severity ?? 1,
              frequency: risk.frequency ?? 1,
              prevention: getLowestPreventionValue(accountRisksSettings),
              RiskFamilyId: familyKey,
              EstablishmentId: activeEstablishment?.id ?? null
            }),
            populationCode: risk.populationCode,
            riskFamilyCode: risk.riskFamilyCode ?? riskFamilyFallbacks[riskIndex],
            riskFamilyName: risk.riskFamilyName
          }
        })
      })
    } catch (error) {
      console.error("Erreur lors de la génération du catalogue DUERP :", error)
      toast("Impossible de générer les risques avec l'assistant.", { type: "error" })
    } finally {
      setIsGenerating(false)
    }
  }

  async function resolveRiskFamilyId(risk: AssistantRisk, riskFamiliesCreated: Map<string, string>) {
    if (props.familyId) return props.familyId

    const riskFamilyKey = risk.RiskFamilyId || risk.riskFamilyCode || risk.riskFamilyName || ""
    const family = riskFamilies.find(
      (item) =>
        item.id === risk.RiskFamilyId ||
        item.code === risk.RiskFamilyId ||
        item.code === risk.riskFamilyCode ||
        matchKey(item.name) === matchKey(riskFamilyKey) ||
        matchKey(item.name) === matchKey(risk.riskFamilyName ?? "")
    )
    if (family) return family.id

    const template = RISK_FAMILY_TEMPLATES.find(
      (item) =>
        item.code === risk.RiskFamilyId ||
        item.code === risk.riskFamilyCode ||
        matchKey(item.name) === matchKey(riskFamilyKey) ||
        matchKey(item.name) === matchKey(risk.riskFamilyName ?? "")
    )
    if (!template) return null

    const familyCreatedKey = template.code
    const riskFamilyCreated = riskFamiliesCreated.get(familyCreatedKey)
    if (riskFamilyCreated) return riskFamilyCreated

    const createdRiskFamily = await dispatch(
      riskFamilyStore.thunks.createEntity(
        createEmptyRiskFamily({
          code: template.code,
          name: template.name
        })
      )
    ).unwrap()
    riskFamiliesCreated.set(familyCreatedKey, createdRiskFamily.id)
    return createdRiskFamily.id
  }

  async function saveGeneratedImport() {
    if (!generatedImport) return
    setIsSaving(true)

    try {
      const populationIdsByCode = new Map<string, string>()
      const riskFamiliesCreated = new Map<string, string>()
      const nextSelectedPopulationIds = new Set(selectedPopulationIds)
      const existingPopulationsByName = new Map(
        existingPopulations.map((population) => [matchKey(population.name), population])
      )

      if (shouldCreatePopulations) {
        for (let i = 0; i < generatedImport.populations.length; i++) {
          const generatedPopulation = generatedImport.populations[i]
          const populationCode = generatedImport.populationCodes[i]
          const existingPopulation = existingPopulationsByName.get(matchKey(generatedPopulation.name))

          if (existingPopulation) {
            if (populationCode) populationIdsByCode.set(populationCode, existingPopulation.id)
            if (!nextSelectedPopulationIds.has(existingPopulation.id)) {
              dispatch(populationStore.actions.select(existingPopulation.id))
              nextSelectedPopulationIds.add(existingPopulation.id)
            }
            continue
          }

          const createdPopulation = await dispatch(populationStore.thunks.createEntity(generatedPopulation)).unwrap()
          if (populationCode) populationIdsByCode.set(populationCode, createdPopulation.id)
          if (!nextSelectedPopulationIds.has(createdPopulation.id)) {
            dispatch(populationStore.actions.select(createdPopulation.id))
            nextSelectedPopulationIds.add(createdPopulation.id)
          }
        }
      }

      const createdRisks: PhysicalRisk[] = []

      for (const generatedRisk of generatedImport.risks) {
        const populationId = isPopulationContext
          ? props.populationId
          : isSiteContext
            ? null
            : populationIdsByCode.get(generatedRisk.populationCode)
        if (!isSiteContext && !populationId) continue
        const { populationCode, riskFamilyCode, riskFamilyName, ...risk } = generatedRisk

        const createdRisk = await dispatch(
          physicalRiskStore.thunks.createEntity({
            ...risk,
            populationIds: populationId ? [populationId] : [],
            siteIds: props.siteId ? [props.siteId] : [],
            RiskFamilyId: await resolveRiskFamilyId(generatedRisk, riskFamiliesCreated)
          })
        ).unwrap()
        createdRisks.push(createdRisk)
      }

      if (shouldCreatePopulations) {
        dispatch(populationStore.actions.setActive(null))
      }

      if (props.onNext && createdRisks[0]) {
        props.onNext(createdRisks[0])
      } else {
        props.onClose()
      }
    } catch (error) {
      console.error("Erreur lors de l'import du catalogue DUERP :", error)
      toast("Impossible d'ajouter les fonctions et risques.", { type: "error" })
    } finally {
      setIsSaving(false)
    }
  }

  function renderRisk(population: PopulationTemplate, risk: PopulationTemplateRisk, riskIndex: number) {
    const riskId = getRiskId(population, riskIndex)
    const isSelected = selectedRisks.includes(riskId)

    return (
      <ListItem key={riskId}>
        <Checkbox active={isSelected} onClick={() => toggleRisk(population, riskIndex)} />

        <div className="flex-1 ml-20">
          <div className="flex items-center">
            <div>{risk.description}</div>
          </div>

          {risk.details && (
            <div className="grey-t text-sm whitespace-pre-line mt-10">
              <div>
                <b>Détails</b>
              </div>
              <div>{risk.details}</div>
            </div>
          )}

          {risk.conséquences && (
            <div className="grey-t text-sm whitespace-pre-line mt-2">
              <div>
                <b>Conséquences</b>
              </div>
              <div>{risk.conséquences}</div>
            </div>
          )}
        </div>

        <Space />

        {risk.riskFamily && (
          <img
            src={getRiskFamilyImage(risk.riskFamily)}
            alt={risk.riskFamily}
            className="w-[80px] h-[80px] object-contain ml-12 shrink-0"
          />
        )}
      </ListItem>
    )
  }

  function handleNext() {
    if (step === "CATEGORIES") {
      startRiskSelection()
      return
    }

    generateImport()
  }

  function renderCategoryButton(category: (typeof categories)[number]) {
    const selectedTemplates = getSelectedCategoryTemplates(category)

    return (
      <CardButton
        key={category.code}
        title={category.label}
        isActive={selectedTemplates.length > 0}
        image={getPopulationImage(category.code)}
        onClick={() => handleSelectCategory(category)}>
        {selectedTemplates.length > 0 && <FontAwesomeIcon icon={faCheck} className="mr-2" />}
      </CardButton>
    )
  }

  function renderCategorySelection() {
    return (
      <div className="mt-20">
        <CardContainer>{categories.map((category) => renderCategoryButton(category))}</CardContainer>
      </div>
    )
  }

  function renderPopulationCategoryModal() {
    if (!populationCategoryModal) return null
    const hasSelectedItems = getSelectedCategoryTemplates(populationCategoryModal).length > 0
    const closePopulationCategoryModal = () => setPopulationCategoryModal(null)

    return (
      <Modal
        title={populationCategoryModal.label}
        onClose={closePopulationCategoryModal}
        onNext={hasSelectedItems ? closePopulationCategoryModal : undefined}
        onNextCta="Continuer"
        isCloseButtonVisible
        disableClickOutside>
        <div className="-mx-40">
          {sortBy(populationCategoryModal.templates, "name").map((population) => (
            <ListItem key={population.code} isSmall isEditable onClick={() => handleSelectPopulation(population)}>
              <Checkbox
                active={selectedItems.includes(population.code)}
                onClick={() => handleSelectPopulation(population)}
              />
              <div className="ml-10">{population.name}</div>
              <Space />
            </ListItem>
          ))}
        </div>
      </Modal>
    )
  }

  function renderPopulationRisks(population: PopulationTemplate) {
    const isSelected = selectedRiskPopulations.includes(population.code)

    return (
      <div key={population.code} className="mb-20 -mx-40">
        <ListItem isEditable onClick={() => togglePopulationAndRisks(population)}>
          <Checkbox active={isSelected} onClick={() => togglePopulationAndRisks(population)} />
          <div className="ml-20 font-bold">{population.name}</div>
          <Space />
        </ListItem>

        <div className="ml-30">
          {population.risks.map((risk, riskIndex) => renderRisk(population, risk, riskIndex))}
        </div>
      </div>
    )
  }

  function renderCategoryRisks(category: PopulationTemplateCategoryWithTemplates) {
    const inrsLink = category.templates.find((population) => population.source)?.source

    return (
      <Card key={category.code} className="mb-20">
        <div className="flex items-center mb-20">
          <img src={getPopulationImage(category.code)} alt={category.label} className="w-[90px] shrink-0" />

          <div className="ml-20">
            <div className="text-lg mb-10">{category.label}</div>
            {inrsLink && (
              <Link onClick={() => window.open(inrsLink, "_blank", "noreferrer")} isWithoutMargin>
                Fiche INRS
              </Link>
            )}
          </div>
        </div>

        {sortBy(category.templates, "name").map((population) => renderPopulationRisks(population))}
      </Card>
    )
  }

  function renderRiskSelection() {
    return <div className="mt-10">{selectedCategories.map((category) => renderCategoryRisks(category))}</div>
  }

  function getConfirmationText() {
    const populationLabel = generatedNewPopulationCount > 1 ? "fonctions" : "fonction"
    const riskCount = generatedImport?.risks.length ?? 0
    const riskLabel = riskCount > 1 ? "risques" : "risque"

    if (generatedNewPopulationCount === 0) {
      return `Souhaitez-vous ajouter ${riskCount} ${riskLabel} ?`
    }

    return `Souhaitez-vous ajouter ${generatedNewPopulationCount} ${populationLabel} et ${riskCount} ${riskLabel} ?`
  }

  return (
    <>
      {isGenerating && (
        <LoadingModal owlAssistantText="Je m'occupe de mettre en forme les risques sélectionnés avant de les ajouter." />
      )}
      {generatedImport && (
        <ModalConfirm
          status={isSaving ? "STATUS_SAVING" : undefined}
          onNo={() => {
            setGeneratedImport(null)
          }}
          onYes={() => saveGeneratedImport()}
          textBold={getConfirmationText()}>
          <p className="grey-t">{"Vous aurez la possibilité de les modifier après l'ajout."}</p>
        </ModalConfirm>
      )}
      <Modal
        isFullScreen
        isLarge
        onClose={() => props.onClose()}
        onNext={
          (step === "CATEGORIES" && selectedItems.length) || (step === "RISKS" && selectedRisks.length)
            ? handleNext
            : undefined
        }
        title="Rechercher depuis le catalogue INRS"
        isCloseButtonVisible
        disableClickOutside>
        {step === "CATEGORIES" && renderCategorySelection()}
        {step === "RISKS" && renderRiskSelection()}
        {renderPopulationCategoryModal()}
      </Modal>
    </>
  )
}
